import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { SubprocessItemRow } from "./OSSubprocessItemsTable";
import RnUzurkuvanjeRequestModal from "./RnUzurkuvanjeRequestModal";
import OSCard from "./OSCard";
import RNMaterialPriemRows, { RNMaterialPriemRowData } from "./RNMaterialPriemRows";
import { useRNMaterialPriemSampleTransfer } from "../hooks/useRNMaterialPriemSampleTransfer";
import type { RNInterProductReceipt } from "../types/Types";
import { useAuth } from "../context/AuthContext";
import { useRNQueuedCellSave } from "../hooks/useRNQueuedCellSave";
import { useToast } from "../context/ToastContext";

export type { RNMaterialPriemRowData as RNMaterialPriemRow } from "./RNMaterialPriemRows";

interface Props {
  rows: RNMaterialPriemRowData[];
  onChange: (rows: SubprocessItemRow[]) => void;
  onChangeAndSave?: (rows: SubprocessItemRow[]) => void;
  isSaving?: boolean;
  leadingActions?: React.ReactNode;
  rnWorkOrderId?: string;
  rnWorkOrderNumber?: string;
  interProductReceipts?: RNInterProductReceipt[];
  onSamplingApproval?: () => void;
}

const RNMaterialPriemTable: React.FC<Props> = ({
  rows,
  onChange,
  onChangeAndSave,
  isSaving,
  leadingActions,
  rnWorkOrderId,
  rnWorkOrderNumber,
  interProductReceipts,
  onSamplingApproval,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;
  const [uzurkuvanjeOpen, setUzurkuvanjeOpen] = useState(false);
  const canOpenUzurkuvanje = !!rnWorkOrderId;
  const {
    sampleTransferDisabled,
    sampleTransferTitle,
    handleSampledClick,
  } = useRNMaterialPriemSampleTransfer({
    rows,
    rnWorkOrderId,
    rnWorkOrderNumber,
    interProductReceipts,
  });

  const patchActual = (index: number, patch: Partial<SubprocessItemRow>) => {
    onChange(rowsRef.current.map((row, rowIndex) => (
      rowIndex === index ? { ...row, ...patch } : row
    )));
  };

  const saveRows = useCallback((next: RNMaterialPriemRowData[]) => {
    (onChangeAndSave ?? onChange)(next);
  }, [onChange, onChangeAndSave]);
  const { saveOrQueue, isQueued } = useRNQueuedCellSave({
    value: rows,
    isSaving,
    scopeKey: rows.map((row) => row.rowId || '').sort().join(','),
    onSave: saveRows,
    onDiscard: () => showToast(t('os.materialno.queuedCellSaveDiscarded'), 'info'),
  });
  const patchActualAndSave = (index: number, patch: Partial<SubprocessItemRow>) => {
    const rowKey = rowsRef.current[index]?.rowId;
    if (!rowKey) return;
    saveOrQueue(rowKey, (current) => current.map((row) => (
      row.rowId === rowKey ? { ...row, ...patch } : row
    )));
  };

  return (
    <OSCard
      title={t('os.materialno.priemTitle')}
      actions={(
        <>
          <button
            type="button"
            onClick={() => setUzurkuvanjeOpen(true)}
            disabled={!canOpenUzurkuvanje}
            title={!canOpenUzurkuvanje ? (t('rnUzurkuvanjeRequests.saveFirst') || undefined) : undefined}
            className={`inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium shadow-sm focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${
              canOpenUzurkuvanje
                ? 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50'
                : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {t('rnUzurkuvanjeRequests.openButton')}
          </button>
          <span
            className="inline-flex"
            title={sampleTransferTitle}
          >
            <button
              type="button"
              onClick={handleSampledClick}
              disabled={sampleTransferDisabled}
              className={`inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium shadow-sm focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/30 focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-600/50 ${
                !sampleTransferDisabled
                  ? 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50'
                  : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('rnUzurkuvanjeRequests.sampledButton')}
            </button>
          </span>
          {leadingActions}
        </>
      )}
    >
      <RNMaterialPriemRows
        rows={rows}
        isSaving={isSaving}
        currentUserId={user?._id}
        onPatchActual={patchActual}
        onPatchActualAndSave={patchActualAndSave}
        isSaveQueued={isQueued}
      />
      <RnUzurkuvanjeRequestModal
        isOpen={uzurkuvanjeOpen}
        onClose={() => setUzurkuvanjeOpen(false)}
        onApprovalChange={onSamplingApproval}
        rnWorkOrderId={rnWorkOrderId || ''}
        rnWorkOrderNumber={rnWorkOrderNumber || ''}
        rows={rows}
      />
    </OSCard>
  );
};

export default RNMaterialPriemTable;
